import { Alert, Autocomplete, Box, MenuItem, Stack, TextField } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from './Dialogs/FormDialog';
import { GeoLocationMultiSelect } from './GeoLocationMultiSelect';
import { LineOfBizSelect } from '../../modules/lines-of-business/LineOfBizSelect';
import { TenantBrandTreeMultiSelect } from '../../modules/system/TenantBrandTreeMultiSelect';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { useTenant } from '../../core/contexts/TenantContext';
import type {
  AIModel,
  LineOfBiz,
  LineOfBizBrand,
  LineOfBizStatus,
} from '../../core/types/data-models';

const STATUS_OPTIONS: { value: LineOfBizStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'DISABLED', label: 'Disabled' },
];

// The 50 most-spoken languages as ISO 639-1 codes, roughly ordered by number
// of speakers. The picker stores/show the bare code (matches `Prompt.language`);
// LANGUAGE_NAMES drives the human-readable label in the dropdown. freeSolo still
// lets a user type any other ISO code not in this list.
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', zh: 'Chinese', hi: 'Hindi', es: 'Spanish', fr: 'French',
  ar: 'Arabic', bn: 'Bengali', pt: 'Portuguese', ru: 'Russian', ur: 'Urdu',
  id: 'Indonesian', de: 'German', ja: 'Japanese', pa: 'Punjabi', ko: 'Korean',
  it: 'Italian', tr: 'Turkish', vi: 'Vietnamese', ta: 'Tamil', te: 'Telugu',
  mr: 'Marathi', fa: 'Persian', th: 'Thai', gu: 'Gujarati', pl: 'Polish',
  uk: 'Ukrainian', ml: 'Malayalam', kn: 'Kannada', nl: 'Dutch', sw: 'Swahili',
  ro: 'Romanian', el: 'Greek', cs: 'Czech', hu: 'Hungarian', sv: 'Swedish',
  he: 'Hebrew', da: 'Danish', fi: 'Finnish', no: 'Norwegian', sk: 'Slovak',
  bg: 'Bulgarian', hr: 'Croatian', sr: 'Serbian', lt: 'Lithuanian', sl: 'Slovenian',
  et: 'Estonian', lv: 'Latvian', ms: 'Malay', tl: 'Tagalog', ha: 'Hausa',
};
const LANGUAGE_OPTIONS: string[] = Object.keys(LANGUAGE_NAMES);

interface NewLineOfBizDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the newly created LOB after a successful POST. */
  onCreated?: (lob: LineOfBiz) => void;
  /** Called with the updated LOB after a successful PUT. */
  onSaved?: (lob: LineOfBiz) => void;
  /**
   * When provided, the dialog opens in *edit* mode: form fields are
   * seeded from this row and the submit hits PUT on the same endpoint
   * instead of POST. Switch to `undefined` for the create flow.
   */
  editing?: LineOfBiz | null;
}

/**
 * LineOfBiz create/edit dialog. Originally a creation-only helper for
 * the sidebar's "New line of business" entry; extended in 2026-05 to
 * back the Edit button on the LOB Dashboard's Details card so both
 * code paths use the same form.
 *
 * Shape: three fields (name, description, parent). Invalidates the
 * shared `['lines-of-business']` cache on success so every reader (the
 * switcher, the LinesOfBusiness page, the parent-picker) updates
 * without a manual refetch. `tenantId` is injected from the active
 * tenant context for create (the Prisma tenant extension also fills
 * it on the backend, but sending it explicitly keeps the create
 * payload predictable).
 */
export function NewLineOfBizDialog({
  open,
  onClose,
  onCreated,
  onSaved,
  editing,
}: NewLineOfBizDialogProps) {
  const { activeTenantId } = useTenant();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [website, setWebsite] = useState('');
  const [status, setStatus] = useState<LineOfBizStatus>('ACTIVE');
  const [parentId, setParentId] = useState<number | null>(null);
  const [brandIds, setBrandIds] = useState<number[]>([]);
  const [supportedGeoLocationIds, setSupportedGeoLocationIds] = useState<number[]>([]);
  const [supportedAiModelIds, setSupportedAiModelIds] = useState<number[]>([]);
  const [supportedLanguages, setSupportedLanguages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Catalog of AIModels for the "Supported AI models" multi-select.
  // Cached under the same key the rest of the app uses so the picker
  // shares the fetch with other dialogs.
  const aiModelsQuery = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 200, sort: 'aiEngine' }),
  });
  const aiModelOptions = aiModelsQuery.data?.data ?? [];
  const aiModelById = useMemo(() => {
    const map = new Map<number, AIModel>();
    for (const m of aiModelOptions) map.set(m.id, m);
    return map;
  }, [aiModelOptions]);
  const selectedAiModels = useMemo(
    () =>
      supportedAiModelIds
        .map((id) => aiModelById.get(id))
        .filter((m): m is AIModel => m != null),
    [supportedAiModelIds, aiModelById],
  );

  const isEdit = editing != null;

  // Existing brand attachments — only fetched when the dialog is open in
  // edit mode. Used both to seed the multi-select and to compute the
  // attach/detach delta on submit.
  // Only OWNER associations belong in this multi-select — COMPETITOR /
  // SUBSIDIARY / PARTNER attachments are managed from their own views
  // (e.g. the Competitor Brands page) and should not be wiped or
  // re-seeded by saving this dialog.
  const existingBrandLinks = useQuery({
    queryKey: [
      'line-of-biz-brands',
      { lineOfBizId: editing?.id, associationType: 'OWNER' },
    ],
    queryFn: () =>
      resources.lineOfBizBrandsFor(editing!.id).list({
        pageSize: 500,
        filter: { associationType: 'OWNER' },
      }),
    enabled: open && isEdit,
  });
  const existingBrandIds: number[] =
    (existingBrandLinks.data?.data as LineOfBizBrand[] | undefined)?.map(
      (r) => r.tenantBrandId,
    ) ?? [];

  // Reset / seed every time the dialog re-opens. We don't clear on
  // close so an accidental dismissal mid-edit doesn't wipe the user's
  // typing the next time they re-open — but on open we always reset
  // to the row's values (or blanks for create).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name ?? '');
      setDescription(editing.description ?? '');
      setWebsite(editing.website ?? '');
      setStatus(editing.status ?? 'ACTIVE');
      setParentId(editing.parentId ?? null);
      setSupportedGeoLocationIds(editing.supportedGeoLocationIds ?? []);
      setSupportedAiModelIds(editing.supportedAiModelIds ?? []);
      setSupportedLanguages(editing.supportedLanguages ?? []);
      // brandIds gets seeded below when the junction query lands.
    } else {
      setName('');
      setDescription('');
      setWebsite('');
      setStatus('ACTIVE');
      setParentId(null);
      setBrandIds([]);
      setSupportedGeoLocationIds([]);
      setSupportedAiModelIds([]);
      setSupportedLanguages([]);
    }
    setError(null);
  }, [open, editing]);

  // Seed the brand multi-select once the existing-links query resolves
  // in edit mode. We don't reset on every render because the user may
  // have already edited the selection.
  useEffect(() => {
    if (!open || !isEdit) return;
    if (existingBrandLinks.data) {
      setBrandIds(existingBrandIds);
    }
    // existingBrandIds derived from data so depending on data is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, existingBrandLinks.data]);

  const create = useMutation({
    mutationFn: (payload: Partial<LineOfBiz>) => resources.linesOfBusiness.create(payload),
  });

  const update = useMutation({
    mutationFn: (payload: { id: number; data: Partial<LineOfBiz> }) =>
      resources.linesOfBusiness.update(payload.id, payload.data),
  });

  const [syncing, setSyncing] = useState(false);
  const loading = create.isPending || update.isPending || syncing;

  /**
   * Diff the selected tenant-brand ids against the previously attached
   * ones and issue the matching PUT (attach) / DELETE (detach) calls
   * against the `/lines-of-business/:id/brands/:tenantBrandId`
   * junction. No-op when the selection is unchanged.
   */
  const syncBrandLinks = async (lobId: number, previousBrandIds: number[]) => {
    const previous = new Set(previousBrandIds);
    const next = new Set(brandIds);
    const toAttach = brandIds.filter((id) => !previous.has(id));
    const toDetach = previousBrandIds.filter((id) => !next.has(id));
    if (toAttach.length === 0 && toDetach.length === 0) return;
    const junction = resources.lineOfBizBrandsFor(lobId);
    await Promise.all([
      // Explicit OWNER — this dialog only manages owner brands.
      ...toAttach.map((tenantBrandId) =>
        junction.update(tenantBrandId, { associationType: 'OWNER' }),
      ),
      ...toDetach.map((tenantBrandId) => junction.remove(tenantBrandId)),
    ]);
    queryClient.invalidateQueries({
      queryKey: ['line-of-biz-brands', { lineOfBizId: lobId }],
    });
    // Cards that read the LOB with ?include=lineOfBizBrands also need to
    // refresh — invalidate the broader cache key.
    queryClient.invalidateQueries({ queryKey: ['lines-of-business'] });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    try {
      // Industries / industry-segments are managed from the LOB
      // dashboard's "Analyze and define Industries" flow, not here —
      // omit them from the payload so existing values survive untouched.
      const payload: Partial<LineOfBiz> = {
        name: name.trim(),
        description: description.trim(),
        website: website.trim(),
        status,
        parentId: parentId,
        supportedGeoLocationIds,
        supportedAiModelIds,
        supportedLanguages,
      };
      if (isEdit && editing) {
        const saved = await update.mutateAsync({ id: editing.id, data: payload });
        setSyncing(true);
        try {
          await syncBrandLinks(editing.id, existingBrandIds);
        } finally {
          setSyncing(false);
        }
        queryClient.invalidateQueries({ queryKey: ['lines-of-business'] });
        onSaved?.(saved);
      } else {
        const created = await create.mutateAsync({
          ...payload,
          // Match what `LinesOfBusinessPage` sends so the row lands on the
          // same tenant the user is currently scoped to.
          ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
        });
        if (brandIds.length > 0) {
          setSyncing(true);
          try {
            await syncBrandLinks(created.id, []);
          } finally {
            setSyncing(false);
          }
        }
        queryClient.invalidateQueries({ queryKey: ['lines-of-business'] });
        onCreated?.(created);
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Edit line of business' : 'New line of business'}
      submitLabel={isEdit ? 'Save' : 'Create'}
      loading={loading}
      maxWidth="md"
      onClose={() => {
        if (loading) return;
        onClose();
      }}
      onSubmit={handleSubmit}
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Stack spacing={2}>
        <TextField
          label="Name"
          required
          autoFocus
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description"
          multiline
          minRows={2}
          fullWidth
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <TextField
          label="Website"
          fullWidth
          placeholder="https://example.com"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
        <LineOfBizSelect
          value={parentId}
          onChange={setParentId}
          // Editing a LOB → exclude its own subtree from the parent
          // picker so the user can't pick a descendant (or itself).
          excludeId={editing?.id}
        />
        <TenantBrandTreeMultiSelect
          value={brandIds}
          onChange={setBrandIds}
          label="Brands"
          helperText="Brands attached to this line of business. Pick from any organization."
        />
        {/* Geo locations and languages sit on the same row — both are
            multi-select chip pickers and pair naturally as the LOB's
            targeting scope. Stack falls back to vertical on narrow
            viewports. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <GeoLocationMultiSelect
              value={supportedGeoLocationIds}
              onChange={setSupportedGeoLocationIds}
              label="Supported geo locations"
              helperText="Countries / regions this line of business targets."
            />
          </Box>
          {/* Languages — ISO codes the LOB targets. Same static list the
              PromptsPage uses for its `Prompt.language` picker; freeSolo
              lets a user type extras like 'nl' without pre-seeding them
              into the catalog. */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete<string, true, false, true>
              multiple
              freeSolo
              fullWidth
              disableCloseOnSelect
              options={LANGUAGE_OPTIONS}
              value={supportedLanguages}
              isOptionEqualToValue={(a, b) => a === b}
              // Dropdown shows "code — Name" for readability; the selected
              // value (chips) stays the bare ISO code via getOptionLabel.
              getOptionLabel={(code) => code}
              renderOption={(props, code) => (
                <li {...props} key={code}>
                  {code}
                  {LANGUAGE_NAMES[code] ? ` — ${LANGUAGE_NAMES[code]}` : ''}
                </li>
              )}
              onChange={(_, next) =>
                setSupportedLanguages(
                  next
                    .map((v) => v.trim().toLowerCase())
                    .filter((v): v is string => Boolean(v)),
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supported languages"
                  helperText="ISO codes (e.g. en, es). Type a custom code and press Enter to add."
                />
              )}
            />
          </Box>
        </Stack>
        {/* AIModel multi-select — inline because the picker is small
            (the seeded catalog is short) and the chosen value is just
            an int[] of `AIModel.id`. Re-uses the same cache key the
            report-template dialogs use, so it shares one fetch. */}
        <Autocomplete<AIModel, true, false, false>
          multiple
          fullWidth
          disableCloseOnSelect
          options={aiModelOptions}
          value={selectedAiModels}
          loading={aiModelsQuery.isLoading}
          getOptionLabel={(m) => `${m.aiEngine} / ${m.name}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, next) => setSupportedAiModelIds(next.map((m) => m.id))}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Supported AI models"
              helperText="AIModel rows this line of business can route to."
            />
          )}
        />
        <TextField
          label="Status"
          select
          fullWidth
          value={status}
          onChange={(e) => setStatus(e.target.value as LineOfBizStatus)}
          helperText="Lifecycle state. Defaults to Active."
        >
          {STATUS_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
    </FormDialog>
  );
}
